import React, { useContext, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import SidebarContext from "../../contexts/MyContext";
import Modal from "../../components/Modal/Modal";
import { TtsHandler, TTS_MAX_TEXT_LENGTH } from "../../Apihander/Tts/Tts";
import { AudioHandler } from "../../Apihander/AudioRecording/AudioRecording";
import { errorToast, successToast } from "../../utils/notificationUtils";
import { formatTimeInHHMMSS } from "../../utils/timeFile";
import { audioBufferToMp3, audioBufferToWav } from "../../utils/adjustAudioUtils";
import { TRANSCRIBE_MAX_DURATION } from "../../utils/transcribeUtils";
import { enablePushNotifications } from "../../utils/pushNotification";
import NotesTool, { NOTES_IMPORT_BUSY } from "../Notes/NotesTool";
import { recordingSeconds } from "../Notes/notesShared";
import RecordingPickerModal from "./RecordingPickerModal";
import TranslateLanguage from "./TranslateLanguage";
import TextToSpeech from "./TextToSpeech";
import { AiIc } from "./aiShared";
import "../Notes/notes.css";
import "./aiTools.css";

// Every audio tool opens the same picker; only its wording changes.
const PICKER_CONTEXT = {
    transcribe: { title: "Choose a recording to transcribe", cta: "Transcribe", tone: "indigo" },
    summary: { title: "Choose a recording to summarise", cta: "Summarise", tone: "teal" },
    adjust: { title: "Choose a recording to adjust", cta: "Open in Audio Adjuster", tone: "blue" },
    trim: { title: "Choose a recording to trim", cta: "Open in Trim", tone: "cyan" },
    translate: { title: "Choose audio to translate", cta: "Continue", tone: "sky" },
};

const ACCEPTED_TEXT_FILES = ".txt,text/plain";
// caf is left out because browsers cannot decode it for the adjuster.
const ADJUSTABLE_EXTENSIONS = ["mp3", "wav", "m4a", "aac", "ogg", "oga", "webm", "flac", "mp4"];
const ACCEPTED_AUDIO_FILES = ADJUSTABLE_EXTENSIONS.map((ext) => `.${ext}`).join(",");
// m4a is left out because the trim waveform cannot draw it.
const TRIMMABLE_EXTENSIONS = ADJUSTABLE_EXTENSIONS.filter((ext) => ext !== "m4a");
const ACCEPTED_TRIM_FILES = TRIMMABLE_EXTENSIONS.map((ext) => `.${ext}`).join(",");
// m4a is left out because the player this lands on cannot open it.
const TRANSCRIBABLE_EXTENSIONS = ADJUSTABLE_EXTENSIONS.filter((ext) => ext !== "m4a");
const ACCEPTED_TRANSCRIBE_FILES = TRANSCRIBABLE_EXTENSIONS.map((ext) => `.${ext}`).join(",");
// Only containers the browser's own audio decoder can open; avi and mkv are left out because it cannot.
const EXTRACTABLE_VIDEO_EXTENSIONS = ["mp4", "m4v", "mov", "webm", "ogv", "3gp"];
const ACCEPTED_VIDEO_FILES = EXTRACTABLE_VIDEO_EXTENSIONS.map((ext) => `.${ext}`).join(",");
// Decoding happens in memory, so a bigger video would take the tab down with it.
const MAX_VIDEO_SIZE_MB = 200;
// How long to wait for a file's metadata before giving up on reading its duration.
const DURATION_READ_TIMEOUT_MS = 30000;

// One card per tool, so both tools stay visually identical.
const ToolCard = ({ icon, tone, title, description, isLoading, isLocked, onClick }) => (
    <div
        className={`ai-tool vr-tone-${tone}${isLoading ? " is-loading" : ""}`}
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") e.currentTarget.click(); }}
    >
        <div className="ai-tool-top">
            <span className="vr-icon-tile ai-tool-icon" role="img" aria-label={title}>
                <Icon icon={icon} />
            </span>
            {/* The spinner sits where the badge would be so the icon stays visible. */}
            {isLoading && (
                <span className="vr-spinner ai-tool-spinner" role="status">
                    <span className="visually-hidden">Loading...</span>
                </span>
            )}
            {!isLoading && isLocked && (
                <span className="premium-badge ai-premium">
                    <Icon icon="mdi:crown" />
                    PREMIUM
                </span>
            )}
        </div>

        <p className="ai-tool-title">{title}</p>
        <p className="ai-tool-desc">{description}</p>
        <span className="ai-tool-go" aria-hidden="true"><Icon icon="lucide:arrow-right" /></span>
    </div>
);

const AiTools = () => {
    const {
        user, setTabIndex, setFilterAudioData,
        setSelectedAdjustAudio, setSelectedTrimAudio, setActionTabIndex,
        setAudio,
        setTranscribeInProgressIds, setHandoffAudioId,
        setFavoriteAudio, setListTabIndex, setAudioIndex, setSelectedAudioId,
        filterAudioData, favoriteAudio, setTextAreaContent, setSelectedTranscriptAudio, folders,
        // Held in context because this screen unmounts on a tab switch while the job keeps running.
        aiToolBusy: busyTool, setAiToolBusy: setBusyTool,
    } = useContext(SidebarContext);
    const navigate = useNavigate();
    const [notesOpen, setNotesOpen] = useState(false);
    const [pickerTool, setPickerTool] = useState(null);
    const [pickerLoading, setPickerLoading] = useState(false);
    // Each folder with its recordings, since the main list leaves those out.
    const [pickerFolderLists, setPickerFolderLists] = useState([]);
    const [ttsSourceOpen, setTtsSourceOpen] = useState(false);
    const [ttsSource, setTtsSource] = useState(null);
    const [translateAudio, setTranslateAudio] = useState(null);
    const translateAudioInputRef = useRef(null);
    const textInputRef = useRef(null);
    const audioInputRef = useRef(null);
    const trimInputRef = useRef(null);
    const videoInputRef = useRef(null);
    const transcribeInputRef = useRef(null);
    const summaryInputRef = useRef(null);

    const isSubscriptionActive = user?.isSubscriptionActive;
    // The saved Default Audio Format; formats the settings page no longer offers fall back to mp3.
    const defaultFormat = user?.audioFormat === "wav" ? "wav" : "mp3";

    // Text to speech is a paid feature, so unsubscribed users land on the pricing page.
    const handleTextToSpeechClick = () => {
        if (!isSubscriptionActive) {
            navigate("/pricing");
            return;
        }
        if (busyTool) return;
        setTtsSourceOpen(true);
    };

    // The list is read fresh so the picked index matches what the tool screens look up.
    const openPicker = async (tool) => {
        setPickerTool(tool);
        setPickerLoading(true);
        setPickerFolderLists([]);
        try {
            const response = await AudioHandler.doGetFilterRecording();
            const recordings = response?.data?.audio;
            if (Array.isArray(recordings)) {
                setFilterAudioData(recordings);
                setFavoriteAudio(recordings.filter((item) => item?.isFavourite === true));
            }

            const folderList = Array.isArray(response?.data?.folders) ? response.data.folders : folders;
            const liveFolders = (folderList || []).filter((folder) => folder?._id && !folder.isDeleted);
            const folderResponses = await Promise.all(liveFolders.map((folder) => AudioHandler.doGetFolderRecording(folder._id)));
            setPickerFolderLists(liveFolders.map((folder, i) => ({
                folder,
                audio: Array.isArray(folderResponses[i]?.data?.audio) ? folderResponses[i].data.audio : [],
            })));
        } finally {
            setPickerLoading(false);
        }
    };

    // Stays open during its own import, so the upload can be followed from My Notes.
    const handleNoteClick = () => {
        if (busyTool && busyTool !== NOTES_IMPORT_BUSY) return;
        setNotesOpen(true);
    };

    // The audio adjuster is a paid feature, so unsubscribed users land on the pricing page.
    const handleAudioAdjusterClick = () => {
        if (!isSubscriptionActive) {
            navigate("/pricing");
            return;
        }
        if (busyTool) return;
        openPicker("adjust");
    };

    // Trimming is part of the free plan, so no subscription check here.
    const handleTrimAudioClick = () => {
        if (busyTool) return;
        openPicker("trim");
    };

    // Translating is a paid feature, so unsubscribed users land on the pricing page.
    const handleTranslateClick = () => {
        if (!isSubscriptionActive) {
            navigate("/pricing");
            return;
        }
        if (busyTool) return;
        openPicker("translate");
    };

    // Transcription is a paid feature, so unsubscribed users land on the pricing page.
    const handleTranscribeClick = () => {
        if (!isSubscriptionActive) {
            navigate("/pricing");
            return;
        }
        if (busyTool) return;
        openPicker("transcribe");
    };

    // The summary comes out of the same paid job, so unsubscribed users land on the pricing page.
    const handleSummaryClick = () => {
        if (!isSubscriptionActive) {
            navigate("/pricing");
            return;
        }
        if (busyTool) return;
        openPicker("summary");
    };

    // Extracting audio is a paid feature, so unsubscribed users land on the pricing page.
    const handleExtractAudioClick = () => {
        if (!isSubscriptionActive) {
            navigate("/pricing");
            return;
        }
        if (busyTool) return;
        videoInputRef.current?.click();
    };

    const readTextFile = (file) =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(String(reader.result || ""));
            reader.onerror = () => reject(reader.error);
            reader.readAsText(file);
        });

    const base64ToBlob = (base64, mimeType) => {
        const binary = atob(base64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
            bytes[i] = binary.charCodeAt(i);
        }
        return new Blob([bytes], { type: mimeType });
    };

    // My Recordings shows a duration, so it is read off the generated file.
    const readDuration = (blob) =>
        new Promise((resolve) => {
            const objectUrl = URL.createObjectURL(blob);
            const audio = new Audio();
            let settled = false;

            const finish = (seconds) => {
                if (settled) return;
                settled = true;
                clearTimeout(timeoutId);
                URL.revokeObjectURL(objectUrl);
                resolve(Number.isFinite(seconds) && seconds > 0 ? seconds : 0);
            };

            // A container the browser never reports metadata for would otherwise leave the tool spinning forever.
            const timeoutId = setTimeout(() => finish(0), DURATION_READ_TIMEOUT_MS);

            audio.onloadedmetadata = () => finish(audio.duration);
            audio.onerror = () => finish(0);
            audio.src = objectUrl;
        });

    // Saves through the normal upload route so the audio lands in My Recordings.
    const uploadRecording = (blob, fileName, duration) => {
        const formData = new FormData();
        formData.append("audiofile", blob, fileName);
        formData.append("timeDuration", formatTimeInHHMMSS(duration));
        formData.append("action", "Audio");
        formData.append("isFavourite", false);
        formData.append("hasnote", "false");
        // Lets the backend suffix the name instead of rejecting a duplicate.
        formData.append("isRename", "true");

        return AudioHandler.doUploadAudioUrl(formData);
    };

    const readErrorMessage = (result) =>
        result?.response?.data?.error ||
        result?.response?.data?.message ||
        result?.message ||
        "An error occurred";

    // The file's text lands in the editor, where it can be changed before generating.
    const handleTextFileChange = async (e) => {
        const file = e.target.files?.[0];
        // Cleared so picking the same file again still fires a change event.
        e.target.value = "";
        if (!file) return;

        const isTextFile = /\.txt$/i.test(file.name) || file.type === "text/plain";
        if (!isTextFile) {
            errorToast("Please upload a plain text (.txt) file");
            return;
        }

        try {
            const text = (await readTextFile(file)).trim();

            if (!text) {
                errorToast("The selected file is empty");
                return;
            }
            if (text.length > TTS_MAX_TEXT_LENGTH) {
                errorToast(`Text must be ${TTS_MAX_TEXT_LENGTH} characters or less`);
                return;
            }

            setTtsSource({ label: file.name, text, fromFile: true, key: Date.now() });
            successToast("Text file imported");
        } catch (error) {
            errorToast(readErrorMessage(error));
        }
    };

    // Returns the saved speech for the result screen, or null once the error is shown.
    const generateSpeech = async (text) => {
        setBusyTool("tts");

        try {
            const speechData = await TtsHandler.doGenerateSpeech({ text, slow: false });

            if (speechData?.statusCode !== 200 || !speechData?.data?.audioBase64) {
                errorToast(readErrorMessage(speechData));
                return null;
            }

            const audioBlob = base64ToBlob(
                speechData.data.audioBase64,
                speechData.data.mimeType || "audio/mpeg"
            );
            const duration = await readDuration(audioBlob);
            const baseName = ttsSource?.fromFile ? ttsSource.label.replace(/\.txt$/i, "") : "";
            const fileName = `${baseName || "Text to speech"}.mp3`;

            const uploaded = await uploadRecording(audioBlob, fileName, duration);

            if (uploaded?.statusCode !== 200) {
                errorToast(readErrorMessage(uploaded));
                return null;
            }

            setFilterAudioData((prevFilterAudioData) => [
                ...prevFilterAudioData,
                uploaded?.data,
            ]);
            return { blob: audioBlob, duration, fileName: uploaded?.data?.audio?.fileName || fileName };
        } catch (error) {
            errorToast(readErrorMessage(error));
            return null;
        } finally {
            setBusyTool(null);
        }
    };

    // An imported file is saved as a normal recording, then continues like a picked one.
    const handleTranslateAudioFileChange = async (e) => {
        const file = e.target.files?.[0];
        // Cleared so picking the same file again still fires a change event.
        e.target.value = "";
        if (!file) return;

        const extension = file.name.split(".").pop()?.toLowerCase();
        if (!TRANSCRIBABLE_EXTENSIONS.includes(extension)) {
            errorToast(`Please upload an audio file (${TRANSCRIBABLE_EXTENSIONS.join(", ")})`);
            return;
        }

        setBusyTool("translate");

        try {
            const duration = await readDuration(file);

            if (duration > TRANSCRIBE_MAX_DURATION) {
                errorToast("Audio longer than 30 minutes cannot be translated. Please trim the recording and try again.");
                return;
            }

            const uploaded = await uploadImportedFile(file, duration);

            if (uploaded?.statusCode !== 200) {
                errorToast(readErrorMessage(uploaded));
                return;
            }

            successToast("Audio imported");
            const { openedAudio } = await loadSavedRecording(uploaded?.data);
            setTranslateAudio(openedAudio);
        } catch (error) {
            errorToast(readErrorMessage(error));
        } finally {
            setBusyTool(null);
        }
    };

    // Nothing is uploaded here: the trim screen works off the local file and saves the trimmed audio itself.
    const handleTrimFileChange = async (e) => {
        const file = e.target.files?.[0];
        // Cleared so picking the same file again still fires a change event.
        e.target.value = "";
        if (!file) return;

        const extension = file.name.split(".").pop()?.toLowerCase();
        if (!TRIMMABLE_EXTENSIONS.includes(extension)) {
            errorToast(`Please upload an audio file (${TRIMMABLE_EXTENSIONS.join(", ")})`);
            return;
        }

        setBusyTool("trim");

        try {
            const duration = await readDuration(file);
            setSelectedTrimAudio({
                // Marks a file that only exists in the browser, so the trim screen saves it as a new recording.
                isLocalUpload: true,
                audio: {
                    fileName: file.name,
                    url: URL.createObjectURL(file),
                    timeDuration: formatTimeInHHMMSS(duration),
                },
            });
            setActionTabIndex(3);
        } catch (error) {
            errorToast(readErrorMessage(error));
        } finally {
            setBusyTool(null);
        }
    };

    const decodeAudioFile = async (file) => {
        const AudioContextClass = window.AudioContext || window.webkitAudioContext;
        const audioContext = new AudioContextClass();

        try {
            const arrayBuffer = await file.arrayBuffer();
            return await audioContext.decodeAudioData(arrayBuffer);
        } finally {
            audioContext.close();
        }
    };

    // The browser's audio decoder reads the audio track straight out of the video container.
    const decodeVideoAudio = async (file) => {
        try {
            return await decodeAudioFile(file);
        } catch {
            throw new Error("We couldn't read any audio from this video. Try an MP4 or WebM file with sound.");
        }
    };

    const encodeDefaultFormat = (audioBuffer) =>
        defaultFormat === "wav" ? audioBufferToWav(audioBuffer) : audioBufferToMp3(audioBuffer);

    // Imported audio is saved in the Default Audio Format, so a file already in it is uploaded as is.
    const uploadImportedFile = async (file, duration) => {
        const extension = file.name.split(".").pop()?.toLowerCase();
        if (extension === defaultFormat) return uploadRecording(file, file.name, duration);

        let audioBuffer;
        try {
            audioBuffer = await decodeAudioFile(file);
        } catch {
            throw new Error(`We couldn't read this file to convert it to ${defaultFormat}.`);
        }
        const baseName = file.name.replace(/\.[^.]+$/, "") || "Imported audio";
        const blob = await encodeDefaultFormat(audioBuffer);
        return uploadRecording(blob, `${baseName}.${defaultFormat}`, duration);
    };

    const handleVideoFileChange = async (e) => {
        const file = e.target.files?.[0];
        // Cleared so picking the same file again still fires a change event.
        e.target.value = "";
        if (!file) return;

        const extension = file.name.split(".").pop()?.toLowerCase();
        if (!EXTRACTABLE_VIDEO_EXTENSIONS.includes(extension)) {
            errorToast(`Please upload a video file (${EXTRACTABLE_VIDEO_EXTENSIONS.join(", ")})`);
            return;
        }
        if (file.size > MAX_VIDEO_SIZE_MB * 1024 * 1024) {
            errorToast(`Video must be ${MAX_VIDEO_SIZE_MB}MB or less`);
            return;
        }

        setBusyTool("extract");

        try {
            const audioBuffer = await decodeVideoAudio(file);

            if (!audioBuffer?.duration) {
                errorToast("This video has no audio to extract");
                return;
            }

            const audioBlob = await encodeDefaultFormat(audioBuffer);
            const recordingName = file.name.replace(/\.[^.]+$/, "") || "Extracted audio";

            const uploaded = await uploadRecording(audioBlob, `${recordingName}.${defaultFormat}`, audioBuffer.duration);

            if (uploaded?.statusCode === 200) {
                successToast(uploaded?.message || "Audio extracted successfully");
                setFilterAudioData((prevFilterAudioData) => [
                    ...prevFilterAudioData,
                    uploaded?.data,
                ]);
                setTabIndex(2);
            } else {
                errorToast(readErrorMessage(uploaded));
            }
        } catch (error) {
            errorToast(readErrorMessage(error));
        } finally {
            setBusyTool(null);
        }
    };

    // The player plays the record at the selected index, so the saved recording is read back from the list.
    const loadSavedRecording = async (savedAudio, folderId) => {
        const response = await AudioHandler.doGetFilterRecording(folderId);
        const recordings = response?.data?.audio;

        if (!Array.isArray(recordings)) {
            setFilterAudioData((prevFilterAudioData) => [...prevFilterAudioData, savedAudio]);
            return { openedAudio: savedAudio, index: -1 };
        }

        setFilterAudioData(recordings);
        setFavoriteAudio(recordings.filter((item) => item?.isFavourite === true));

        const index = recordings.findIndex((item) => item?._id === savedAudio?._id);

        return { openedAudio: index >= 0 ? recordings[index] : savedAudio, index };
    };

    // The file is on the backend, so only its id is sent; the running job is picked up by the context poll.
    const startTranscription = async (audioId) => {
        const started = await AudioHandler.doHandalStartTranscribeAudio({
            audioId,
            uploadRequired: false,
        });

        if (started?.statusCode === 200) {
            setTranscribeInProgressIds((prevIds) =>
                prevIds.includes(audioId) ? prevIds : [...prevIds, audioId]
            );
            return true;
        }
        if (started?.statusCode === 400 || started?.response?.data?.statusCode === 400 || started?.response?.status === 400) {
            errorToast("Transcription has failed as speech is not detected in the audio.");
        } else {
            errorToast(started?.response?.data?.message || "Unable to start the transcription. Please try again.");
        }
        return false;
    };

    // The player is where a running transcription is shown.
    const openInPlayer = async (savedAudio, folderId) => {
        const { openedAudio, index } = await loadSavedRecording(savedAudio, folderId);

        setActionTabIndex(null);
        // Favourites would hide the new recording, so the full list is shown.
        setListTabIndex(1);

        if (index >= 0) {
            setAudioIndex(index);
            setSelectedAudioId(openedAudio?._id);
        }

        setAudio(openedAudio);
        setHandoffAudioId(openedAudio?._id);
        setTabIndex(folderId || 2);
    };

    // The recording is saved first, then transcribed, and the player is where the running job is shown.
    // One job writes both the transcript and the summary, so the Transcribe and Summary cards share it.
    const handleTranscribeFileChange = async (e, tool) => {
        const file = e.target.files?.[0];
        // Cleared so picking the same file again still fires a change event.
        e.target.value = "";
        if (!file) return;

        const extension = file.name.split(".").pop()?.toLowerCase();
        if (!TRANSCRIBABLE_EXTENSIONS.includes(extension)) {
            errorToast(`Please upload an audio file (${TRANSCRIBABLE_EXTENSIONS.join(", ")})`);
            return;
        }

        // Asked while the file picker's gesture is still fresh, since a prompt needs one.
        enablePushNotifications();

        setBusyTool(tool);

        try {
            const duration = await readDuration(file);

            if (duration > TRANSCRIBE_MAX_DURATION) {
                errorToast("Audio longer than 30 minutes cannot be transcribed. Please trim the recording and try again.");
                return;
            }

            const uploaded = await uploadImportedFile(file, duration);

            if (uploaded?.statusCode !== 200) {
                errorToast(readErrorMessage(uploaded));
                return;
            }

            const savedAudio = uploaded?.data;

            await startTranscription(savedAudio?._id);
            // The recording is saved either way, so it is opened in My Recordings.
            await openInPlayer(savedAudio);
        } catch (error) {
            errorToast(readErrorMessage(error));
        } finally {
            setBusyTool(null);
        }
    };

    // Nothing is uploaded here: the adjust screen works off the local file and saves the adjusted audio itself.
    const handleAudioFileChange = async (e) => {
        const file = e.target.files?.[0];
        // Cleared so picking the same file again still fires a change event.
        e.target.value = "";
        if (!file) return;

        const extension = file.name.split(".").pop()?.toLowerCase();
        if (!ADJUSTABLE_EXTENSIONS.includes(extension)) {
            errorToast(`Please upload an audio file (${ADJUSTABLE_EXTENSIONS.join(", ")})`);
            return;
        }

        setBusyTool("adjust");

        try {
            const duration = await readDuration(file);
            setSelectedAdjustAudio({
                // Marks a file that only exists in the browser, so the adjust screen saves it as a new recording.
                isLocalUpload: true,
                audio: {
                    fileName: file.name,
                    url: URL.createObjectURL(file),
                    timeDuration: formatTimeInHHMMSS(duration),
                },
            });
            setActionTabIndex(7);
        } catch (error) {
            errorToast(readErrorMessage(error));
        } finally {
            setBusyTool(null);
        }
    };

    // Import keeps each tool's own file flow; the dialog opens inside the same click.
    const handlePickerImport = () => {
        const inputs = {
            transcribe: transcribeInputRef,
            summary: summaryInputRef,
            adjust: audioInputRef,
            trim: trimInputRef,
            translate: translateAudioInputRef,
        };
        const input = inputs[pickerTool];
        setPickerTool(null);
        input?.current?.click();
    };

    // A picked recording goes to the same screen the recording list opens for it.
    const handlePickerContinue = async (picked) => {
        const tool = pickerTool;
        setPickerTool(null);
        if (!picked) return;

        // A folder recording is opened from its folder's list, as the dashboard does inside that folder.
        const inMainList = filterAudioData.some((item) => item?._id === picked._id);
        const folderEntry = inMainList ? null : pickerFolderLists.find((entry) => entry.audio.some((item) => item?._id === picked._id));
        const list = folderEntry ? folderEntry.audio : filterAudioData;
        if (folderEntry) {
            setFilterAudioData(list);
            setFavoriteAudio(list.filter((item) => item?.isFavourite === true));
        }

        const index = list.findIndex((item) => item?._id === picked._id);
        const selectInList = () => {
            // The tool screens read the record at this index back from the full list.
            setListTabIndex(1);
            setAudioIndex(index);
            setSelectedAudioId(picked._id);
        };

        if (tool === "translate") {
            setTranslateAudio(picked);
            return;
        }
        if (tool === "adjust") {
            selectInList();
            setSelectedAdjustAudio(picked);
            setActionTabIndex(7);
            return;
        }
        if (tool === "trim") {
            selectInList();
            setSelectedTrimAudio(picked);
            setActionTabIndex(3);
            return;
        }

        // Transcribe and Summary: a finished transcript is shown, otherwise the job is started.
        if (picked?.transcribe?.isTranscribe) {
            selectInList();
            setTextAreaContent(picked.transcribe.transcribeText);
            setSelectedTranscriptAudio(picked);
            setActionTabIndex(6);
            return;
        }
        if (recordingSeconds(picked) > TRANSCRIBE_MAX_DURATION) {
            errorToast("Audio longer than 30 minutes cannot be transcribed. Please trim the recording and try again.");
            return;
        }

        // Asked on this click, because a browser ignores a permission prompt without a user gesture.
        enablePushNotifications();
        setBusyTool(tool);
        try {
            if (picked?.transcribe?.status !== "processing") await startTranscription(picked._id);
            await openInPlayer(picked, folderEntry?.folder._id);
        } catch (error) {
            errorToast(readErrorMessage(error));
        } finally {
            setBusyTool(null);
        }
    };

    // The latest copy from the lists, so the translate screen sees the transcript as soon as the poll writes it.
    const translateRecord = translateAudio
        ? [...(filterAudioData || []), ...(favoriteAudio || [])].find((item) => item?._id === translateAudio._id) || translateAudio
        : null;

    // Starts the transcript a translation needs; false when it could not be started.
    const handleTranslateTranscribe = async () => {
        if (recordingSeconds(translateRecord) > TRANSCRIBE_MAX_DURATION) {
            errorToast("Audio longer than 30 minutes cannot be translated. Please trim the recording and try again.");
            return false;
        }
        enablePushNotifications();
        try {
            const audioId = translateRecord._id;
            const started = translateRecord?.transcribe?.status === "processing" || await startTranscription(audioId);
            // Marked running at once, so a failure left from an earlier attempt is not read as this one.
            const markRunning = (item) => (item?._id === audioId ? { ...item, transcribe: { ...item.transcribe, status: "processing" } } : item);
            if (started) {
                setFilterAudioData((prevData) => (prevData || []).map(markRunning));
                setFavoriteAudio((prevFavorites) => (prevFavorites || []).map(markRunning));
            }
            return started;
        } catch (error) {
            errorToast(readErrorMessage(error));
            return false;
        }
    };

    const openTtsEditor = () => {
        setTtsSourceOpen(false);
        setTtsSource({ label: "Typed text", text: "", key: Date.now() });
    };

    const importTtsText = () => {
        setTtsSourceOpen(false);
        textInputRef.current?.click();
    };

    if (notesOpen) {
        return (
            <NotesTool
                onClose={() => setNotesOpen(false)}
                importExtensions={ADJUSTABLE_EXTENSIONS}
                readDuration={readDuration}
                uploadImportedFile={uploadImportedFile}
            />
        );
    }

    const fileInputs = (
        <>
            <input
                type="file"
                ref={textInputRef}
                accept={ACCEPTED_TEXT_FILES}
                style={{ display: "none" }}
                onChange={handleTextFileChange}
            />
            <input
                type="file"
                ref={translateAudioInputRef}
                accept={ACCEPTED_TRANSCRIBE_FILES}
                style={{ display: "none" }}
                onChange={handleTranslateAudioFileChange}
            />
            <input
                type="file"
                ref={audioInputRef}
                accept={ACCEPTED_AUDIO_FILES}
                style={{ display: "none" }}
                onChange={handleAudioFileChange}
            />
            <input
                type="file"
                ref={trimInputRef}
                accept={ACCEPTED_TRIM_FILES}
                style={{ display: "none" }}
                onChange={handleTrimFileChange}
            />
            <input
                type="file"
                ref={videoInputRef}
                accept={ACCEPTED_VIDEO_FILES}
                style={{ display: "none" }}
                onChange={handleVideoFileChange}
            />

            <input
                type="file"
                ref={transcribeInputRef}
                accept={ACCEPTED_TRANSCRIBE_FILES}
                style={{ display: "none" }}
                onChange={(e) => handleTranscribeFileChange(e, "transcribe")}
            />
            <input
                type="file"
                ref={summaryInputRef}
                accept={ACCEPTED_TRANSCRIBE_FILES}
                style={{ display: "none" }}
                onChange={(e) => handleTranscribeFileChange(e, "summary")}
            />
        </>
    );

    const rootRecordings = (filterAudioData || []).filter((audio) => audio?._id);
    const rootIds = new Set(rootRecordings.map((audio) => audio._id));
    const pickerRecordings = [
        ...rootRecordings,
        ...pickerFolderLists.flatMap((entry) => entry.audio).filter((audio) => audio?._id && !rootIds.has(audio._id)),
    ];

    const modals = (
        <>
            {pickerTool && (
                <RecordingPickerModal
                    {...PICKER_CONTEXT[pickerTool]}
                    recordings={pickerRecordings}
                    loading={pickerLoading}
                    onImport={handlePickerImport}
                    onContinue={handlePickerContinue}
                    onClose={() => setPickerTool(null)}
                />
            )}
            {ttsSourceOpen && (
                <Modal onClose={() => setTtsSourceOpen(false)} modaltxt="Text To Speech">
                    <div className="nt nt-modal vr-tone-violet">
                        <p className="ai-sub">Where should the text come from?</p>
                        <button type="button" className="ai-opt" onClick={openTtsEditor}>
                            <span className="ai-oc purple"><AiIc name="edit" size="l" /></span>
                            <span className="nt-grow">
                                <span className="ai-ot">Add or Paste Text</span>
                                <span className="ai-os">Write or paste text manually.</span>
                            </span>
                            <AiIc name="chev" size="s" />
                        </button>
                        <button type="button" className="ai-opt" onClick={importTtsText}>
                            <span className="ai-oc blue"><AiIc name="file" size="l" /></span>
                            <span className="nt-grow">
                                <span className="ai-ot">Import a Text File</span>
                                <span className="ai-os">Choose an existing text file.</span>
                            </span>
                            <AiIc name="chev" size="s" />
                        </button>
                    </div>
                </Modal>
            )}
        </>
    );

    if (ttsSource) {
        return (
            <>
                {fileInputs}
                <TextToSpeech
                    key={ttsSource.key}
                    source={ttsSource}
                    onClose={() => setTtsSource(null)}
                    onGenerate={generateSpeech}
                    onGenerateAgain={() => setTtsSourceOpen(true)}
                />
                {modals}
            </>
        );
    }

    if (translateAudio) {
        return (
            <TranslateLanguage
                audio={translateRecord}
                onBack={() => setTranslateAudio(null)}
                onTranscribe={handleTranslateTranscribe}
            />
        );
    }

    return (
        <div className="ai-page">
            {fileInputs}
            {modals}
            {/* Header matches the "My Recordings" title so both tabs line up. */}
            <div className="ai-hero">
                <span className="ai-hero-mark" aria-hidden="true"><Icon icon="lucide:sparkles" /></span>
                <div className="ai-hero-text">
                    <h4 className="ai-hero-title">
                        <span>
                            AI Tools
                        </span>
                    </h4>
                    <p className="ai-hero-sub">
                        Smart tools to turn your content into audio.
                    </p>
                </div>
            </div>

            <h5 className="ai-group-title">Text & Notes</h5>
            <div className="ai-grid vr-stagger">
                <ToolCard
                    icon="lucide:file-text"
                    tone="indigo"
                    title="Transcribe"
                    description={
                        busyTool === "transcribe"
                            ? "Saving and transcribing..."
                            : "Pick a recording or audio file and we'll write out its transcript and summary."
                    }
                    isLoading={busyTool === "transcribe"}
                    isLocked={!isSubscriptionActive}
                    onClick={handleTranscribeClick}
                />
                <ToolCard
                    icon="lucide:list-checks"
                    tone="teal"
                    title="Summary"
                    description={
                        busyTool === "summary"
                            ? "Saving and summarizing..."
                            : "Pick a recording or audio file and we'll write out its summary."
                    }
                    isLoading={busyTool === "summary"}
                    isLocked={!isSubscriptionActive}
                    onClick={handleSummaryClick}
                />
                <ToolCard
                    icon="lucide:languages"
                    tone="sky"
                    title="Translate"
                    description={
                        busyTool === "translate"
                            ? "Importing audio..."
                            : "Pick a recording or audio file and we'll translate what's said into the language you pick."
                    }
                    isLoading={busyTool === "translate"}
                    isLocked={!isSubscriptionActive}
                    onClick={handleTranslateClick}
                />
                <ToolCard
                    icon="lucide:notebook-pen"
                    tone="amber"
                    title="My Notes"
                    description={
                        busyTool === NOTES_IMPORT_BUSY
                            ? "Importing audio..."
                            : "Browse your notes, or add notes to a recording."
                    }
                    isLoading={busyTool === NOTES_IMPORT_BUSY}
                    onClick={handleNoteClick}
                />
            </div>

            <h5 className="ai-group-title">Audio</h5>
            <div className="ai-grid vr-stagger">
                <ToolCard
                    icon="lucide:scissors"
                    tone="cyan"
                    title="Trim Audio"
                    description={
                        busyTool === "trim"
                            ? "Opening trim screen..."
                            : "Pick a recording or audio file, cut it down and save just the part you need."
                    }
                    isLoading={busyTool === "trim"}
                    onClick={handleTrimAudioClick}
                />
                <ToolCard
                    icon="lucide:sliders-horizontal"
                    tone="blue"
                    title="Audio Adjuster"
                    description={
                        busyTool === "adjust"
                            ? "Opening Audio Adjuster..."
                            : "Pick a recording or audio file to reduce noise, boost volume or change its speed."
                    }
                    isLoading={busyTool === "adjust"}
                    isLocked={!isSubscriptionActive}
                    onClick={handleAudioAdjusterClick}
                />
                <ToolCard
                    icon="lucide:clapperboard"
                    tone="fuchsia"
                    title="Extract Audio"
                    description={
                        busyTool === "extract"
                            ? "Extracting audio..."
                            : "Upload a video and we'll pull the audio out into My Recordings."
                    }
                    isLoading={busyTool === "extract"}
                    isLocked={!isSubscriptionActive}
                    onClick={handleExtractAudioClick}
                />
                <ToolCard
                    icon="lucide:audio-lines"
                    tone="violet"
                    title="Text to Speech"
                    description={
                        busyTool === "tts"
                            ? "Generating speech..."
                            : "Type, paste or import text and we'll read it out loud for you."
                    }
                    isLoading={busyTool === "tts"}
                    isLocked={!isSubscriptionActive}
                    onClick={handleTextToSpeechClick}
                />
            </div>
        </div>
    );
};

export default AiTools;
